/**
 * Google Maps Distance Service
 * Provides accurate driving distances and durations using Google Maps APIs
 */

export interface DistanceResult {
  distance: number; // Distance in miles
  duration: number; // Duration in minutes
  distanceText?: string; // Formatted distance (e.g., "52.3 mi")
  durationText?: string; // Formatted duration (e.g., "1h 10m")
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface RouteLeg extends DistanceResult {}

export interface OptimizedRouteResult {
  currentRoute: DistanceResult;
  currentLegs: RouteLeg[];
  optimizedRoute: DistanceResult;
  optimizedLegs: RouteLeg[];
  waypointOrder: number[];
}

interface CachedDistance extends DistanceResult {
  timestamp: number;
  key: string;
}

class GoogleMapsDistanceService {
  private cache: Map<string, CachedDistance> = new Map();
  private readonly CACHE_TTL = 3600000; // 1 hour in milliseconds
  private directionsService: google.maps.DirectionsService | null = null;
  private distanceMatrixService: google.maps.DistanceMatrixService | null = null;

  /**
   * Initialize the Google Maps services
   */
  private ensureServicesInitialized(): boolean {
    if (typeof google === 'undefined' || !google.maps) {
      return false;
    }

    if (!this.directionsService) {
      this.directionsService = new google.maps.DirectionsService();
    }

    if (!this.distanceMatrixService) {
      this.distanceMatrixService = new google.maps.DistanceMatrixService();
    }

    return true;
  }

  /**
   * Generate cache key from coordinates
   */
  private getCacheKey(origin: Coordinates, destination: Coordinates): string {
    return `${origin.lat.toFixed(6)},${origin.lng.toFixed(6)}-${destination.lat.toFixed(6)},${destination.lng.toFixed(6)}`;
  }

  /**
   * Check if cached result is still valid
   */
  private getCachedResult(key: string): DistanceResult | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }

    return {
      distance: cached.distance,
      duration: cached.duration,
      distanceText: cached.distanceText,
      durationText: cached.durationText,
    };
  }

  /**
   * Cache a distance result
   */
  private cacheResult(key: string, result: DistanceResult): void {
    this.cache.set(key, {
      ...result,
      timestamp: Date.now(),
      key,
    });
  }

  /**
   * Convert meters to miles
   */
  private metersToMiles(meters: number): number {
    return meters * 0.000621371;
  }

  /**
   * Format a miles value as a display string respecting the tenant unit.
   * Internal math always stays in miles; conversion happens only at the display boundary.
   */
  private formatDistanceText(miles: number, unit: 'mi' | 'km'): string {
    if (unit === 'km') {
      return `${(miles * 1.60934).toFixed(1)} km`;
    }
    return `${miles.toFixed(1)} mi`;
  }

  /**
   * Convert seconds to minutes
   */
  private secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  /**
   * Format duration in minutes to readable text
   */
  private formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }

  /**
   * Calculate driving distance and duration between two points using Directions API
   */
  async calculateDrivingDistance(
    origin: Coordinates,
    destination: Coordinates,
    unit: 'mi' | 'km' = 'mi'
  ): Promise<DistanceResult | null> {
    // Check cache first
    const cacheKey = this.getCacheKey(origin, destination);
    const cached = this.getCachedResult(cacheKey);
    if (cached) {
      return cached;
    }

    // Ensure Google Maps is loaded
    if (!this.ensureServicesInitialized()) {
      return null;
    }

    try {

      const result = await new Promise<google.maps.DirectionsResult>(
        (resolve, reject) => {
          this.directionsService!.route(
            {
              origin: new google.maps.LatLng(origin.lat, origin.lng),
              destination: new google.maps.LatLng(destination.lat, destination.lng),
              travelMode: google.maps.TravelMode.DRIVING,
            },
            (response, status) => {
              if (status === google.maps.DirectionsStatus.OK && response) {
                resolve(response);
              } else {
                reject(new Error(`Directions request failed: ${status}`));
              }
            }
          );
        }
      );

      // Extract distance and duration from the first route
      const route = result.routes[0];
      if (!route || !route.legs || route.legs.length === 0) {
        return null;
      }

      const leg = route.legs[0];
      const distanceMeters = leg.distance?.value || 0;
      const durationSeconds = leg.duration?.value || 0;

      // Try to get duration_in_traffic if available (more accurate with current traffic)
      const durationInTrafficSeconds = (leg as any).duration_in_traffic?.value || durationSeconds;

      const distanceMiles = this.metersToMiles(distanceMeters);
      const durationMinutes = this.secondsToMinutes(durationInTrafficSeconds);

      const distanceResult: DistanceResult = {
        distance: distanceMiles,
        duration: durationMinutes,
        distanceText: this.formatDistanceText(distanceMiles, unit),
        durationText: this.formatDuration(durationMinutes),
      };

      // Cache the result
      this.cacheResult(cacheKey, distanceResult);

      return distanceResult;
    } catch (error) {
      console.error('Error calculating distance with Google Maps:', error);
      return null;
    }
  }

  /**
   * Calculate distances for multiple origin-destination pairs using Distance Matrix API
   * More efficient than multiple individual requests
   */
  async calculateDistanceMatrix(
    origins: Coordinates[],
    destinations: Coordinates[],
    unit: 'mi' | 'km' = 'mi'
  ): Promise<(DistanceResult | null)[][]> {
    if (!this.ensureServicesInitialized()) {
      return origins.map(() => destinations.map(() => null));
    }

    // Check cache for all pairs first
    const results: (DistanceResult | null)[][] = [];
    const needsCalculation: { i: number; j: number }[] = [];

    for (let i = 0; i < origins.length; i++) {
      results[i] = [];
      for (let j = 0; j < destinations.length; j++) {
        const cacheKey = this.getCacheKey(origins[i], destinations[j]);
        const cached = this.getCachedResult(cacheKey);
        
        if (cached) {
          results[i][j] = cached;
        } else {
          results[i][j] = null;
          needsCalculation.push({ i, j });
        }
      }
    }

    // If everything is cached, return early
    if (needsCalculation.length === 0) {
      return results;
    }

    try {

      const response = await new Promise<google.maps.DistanceMatrixResponse>(
        (resolve, reject) => {
          this.distanceMatrixService!.getDistanceMatrix(
            {
              origins: origins.map(o => new google.maps.LatLng(o.lat, o.lng)),
              destinations: destinations.map(d => new google.maps.LatLng(d.lat, d.lng)),
              travelMode: google.maps.TravelMode.DRIVING,
            },
            (response, status) => {
              if (status === google.maps.DistanceMatrixStatus.OK && response) {
                resolve(response);
              } else {
                reject(new Error(`Distance Matrix request failed: ${status}`));
              }
            }
          );
        }
      );

      // Process results
      response.rows.forEach((row, i) => {
        row.elements.forEach((element, j) => {
          if (element.status === google.maps.DistanceMatrixElementStatus.OK) {
            const distanceMeters = element.distance.value;
            const durationSeconds = element.duration.value;
            const durationInTrafficSeconds = (element as any).duration_in_traffic?.value || durationSeconds;

            const distanceMiles = this.metersToMiles(distanceMeters);
            const durationMinutes = this.secondsToMinutes(durationInTrafficSeconds);

            const distanceResult: DistanceResult = {
              distance: distanceMiles,
              duration: durationMinutes,
              distanceText: this.formatDistanceText(distanceMiles, unit),
              durationText: this.formatDuration(durationMinutes),
            };

            results[i][j] = distanceResult;

            // Cache the result
            const cacheKey = this.getCacheKey(origins[i], destinations[j]);
            this.cacheResult(cacheKey, distanceResult);
          }
        });
      });

      return results;
    } catch (error) {
      console.error('Error calculating distance matrix:', error);
      return results; // Return partial results (cached ones)
    }
  }

  /**
   * Calculate total route distance and duration through multiple waypoints
   * Uses Directions API with waypoints for most accurate routing
   */
  async calculateRoute(waypoints: Coordinates[], unit: 'mi' | 'km' = 'mi'): Promise<DistanceResult | null> {
    if (waypoints.length < 2) {
      return null;
    }

    if (waypoints.length === 2) {
      // Simple point-to-point
      return this.calculateDrivingDistance(waypoints[0], waypoints[1], unit);
    }

    if (!this.ensureServicesInitialized()) {
      return null;
    }

    try {
      const origin = waypoints[0];
      const destination = waypoints[waypoints.length - 1];
      const intermediateWaypoints = waypoints.slice(1, -1).map(wp => ({
        location: new google.maps.LatLng(wp.lat, wp.lng),
        stopover: true,
      }));

      const result = await new Promise<google.maps.DirectionsResult>(
        (resolve, reject) => {
          this.directionsService!.route(
            {
              origin: new google.maps.LatLng(origin.lat, origin.lng),
              destination: new google.maps.LatLng(destination.lat, destination.lng),
              waypoints: intermediateWaypoints,
              optimizeWaypoints: false, // Keep order as specified
              travelMode: google.maps.TravelMode.DRIVING,
            },
            (response, status) => {
              if (status === google.maps.DirectionsStatus.OK && response) {
                resolve(response);
              } else {
                reject(new Error(`Directions request failed: ${status}`));
              }
            }
          );
        }
      );

      // Sum up all legs
      const route = result.routes[0];
      if (!route || !route.legs) {
        return null;
      }

      let totalDistanceMeters = 0;
      let totalDurationSeconds = 0;

      route.legs.forEach(leg => {
        totalDistanceMeters += leg.distance?.value || 0;
        const durationInTrafficSeconds = (leg as any).duration_in_traffic?.value;
        totalDurationSeconds += durationInTrafficSeconds || leg.duration?.value || 0;
      });

      const distanceMiles = this.metersToMiles(totalDistanceMeters);
      const durationMinutes = this.secondsToMinutes(totalDurationSeconds);

      const routeResult: DistanceResult = {
        distance: distanceMiles,
        duration: durationMinutes,
        distanceText: this.formatDistanceText(distanceMiles, unit),
        durationText: this.formatDuration(durationMinutes),
      };

      return routeResult;
    } catch (error) {
      console.error('Error calculating multi-point route:', error);
      return null;
    }
  }

  /**
   * Calculate optimized route by letting Google reorder intermediate waypoints
   * for minimum total driving distance. Origin and destination remain anchored.
   * Returns both current-order and optimized-order stats plus the reorder mapping.
   */
  async calculateOptimizedRoute(
    origin: Coordinates,
    destination: Coordinates,
    waypoints: Coordinates[],
    unit: 'mi' | 'km' = 'mi'
  ): Promise<OptimizedRouteResult | null> {
    if (waypoints.length === 0) {
      return null;
    }

    if (!this.ensureServicesInitialized()) {
      return null;
    }

    const intermediateWaypoints = waypoints.map(wp => ({
      location: new google.maps.LatLng(wp.lat, wp.lng),
      stopover: true,
    }));

    const makeRequest = (optimize: boolean) =>
      new Promise<google.maps.DirectionsResult>((resolve, reject) => {
        this.directionsService!.route(
          {
            origin: new google.maps.LatLng(origin.lat, origin.lng),
            destination: new google.maps.LatLng(destination.lat, destination.lng),
            waypoints: intermediateWaypoints,
            optimizeWaypoints: optimize,
            travelMode: google.maps.TravelMode.DRIVING,
          },
          (response, status) => {
            if (status === google.maps.DirectionsStatus.OK && response) {
              resolve(response);
            } else {
              reject(new Error(`Directions request failed: ${status}`));
            }
          }
        );
      });

    const extractStats = (result: google.maps.DirectionsResult) => {
      const route = result.routes[0];
      if (!route?.legs) return null;

      let totalDistanceMeters = 0;
      let totalDurationSeconds = 0;
      const legs: RouteLeg[] = [];

      route.legs.forEach(leg => {
        const distMeters = leg.distance?.value || 0;
        const durSeconds = (leg as any).duration_in_traffic?.value || leg.duration?.value || 0;
        totalDistanceMeters += distMeters;
        totalDurationSeconds += durSeconds;
        const distMiles = this.metersToMiles(distMeters);
        const durMinutes = this.secondsToMinutes(durSeconds);
        legs.push({
          distance: distMiles,
          duration: durMinutes,
          distanceText: this.formatDistanceText(distMiles, unit),
          durationText: this.formatDuration(durMinutes),
        });
      });

      const distanceMiles = this.metersToMiles(totalDistanceMeters);
      const durationMinutes = this.secondsToMinutes(totalDurationSeconds);

      return {
        totals: {
          distance: distanceMiles,
          duration: durationMinutes,
          distanceText: this.formatDistanceText(distanceMiles, unit),
          durationText: this.formatDuration(durationMinutes),
        } as DistanceResult,
        legs,
      };
    };

    try {
      const [currentResult, optimizedResult] = await Promise.all([
        makeRequest(false),
        makeRequest(true),
      ]);

      const currentStats = extractStats(currentResult);
      const optimizedStats = extractStats(optimizedResult);

      if (!currentStats || !optimizedStats) return null;

      const waypointOrder = optimizedResult.routes[0]?.waypoint_order || waypoints.map((_, i) => i);

      return {
        currentRoute: currentStats.totals,
        currentLegs: currentStats.legs,
        optimizedRoute: optimizedStats.totals,
        optimizedLegs: optimizedStats.legs,
        waypointOrder,
      };
    } catch (error) {
      console.error('Error calculating optimized route:', error);
      return null;
    }
  }

  /**
   * Clear all cached distances
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}

// Export singleton instance
export const googleMapsDistanceService = new GoogleMapsDistanceService();

